import { useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Timestamp, doc, getDoc, setDoc } from 'firebase/firestore';
import { CheckCircle2, Copy, Loader2, MessageSquare, Timer } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const CODE_LENGTH = 12;
const CODE_TTL_MS = 10 * 60 * 1000;
const MAX_ALLOC_RETRIES = 5;

const generateCode = () => {
  const buf = new Uint32Array(CODE_LENGTH);
  crypto.getRandomValues(buf);
  let out = '';
  for (let i = 0; i < CODE_LENGTH; i++) {
    out += CODE_ALPHABET[buf[i] % CODE_ALPHABET.length];
  }
  return out;
};

const formatRemaining = (sec) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const DiscordLink = () => {
  const { currentUser, userProfile, profileLoading } = useContext(AuthContext);
  const [code, setCode] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!currentUser && !profileLoading) {
    return <Navigate to="/login" replace />;
  }

  const linked = Boolean(userProfile?.discordId);
  const remainingSec = code && expiresAt ? Math.max(0, Math.floor((expiresAt - now) / 1000)) : 0;
  const expired = code && remainingSec === 0;

  const handleGenerate = async () => {
    if (!currentUser) return;
    setGenerating(true);
    try {
      let lastErr = null;
      for (let attempt = 0; attempt < MAX_ALLOC_RETRIES; attempt++) {
        const candidate = generateCode();
        const ref = doc(db, 'discordLinks', candidate);
        const snap = await getDoc(ref);
        if (snap.exists()) continue;
        const exp = new Date(Date.now() + CODE_TTL_MS);
        try {
          await setDoc(ref, {
            uid: currentUser.uid,
            username: userProfile?.username || '',
            createdAt: Timestamp.now(),
            expiresAt: Timestamp.fromDate(exp),
          });
          setCode(candidate);
          setExpiresAt(exp.getTime());
          return;
        } catch (e) {
          lastErr = e;
        }
      }
      throw lastErr || new Error('Could not allocate a code, please try again.');
    } catch (e) {
      toast.error('Failed to generate code', { description: e.message });
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success('Code copied');
    } catch {
      toast.error('Copy failed — select the code manually.');
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 py-8 sm:px-6 sm:py-10">
      <header className="flex flex-col gap-3 border-b border-border/60 pb-6">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          <span className="h-px w-6 bg-heat" />
          <MessageSquare className="h-3 w-3" />
          Discord
        </div>
        <h1 className="font-display text-3xl font-bold leading-none tracking-tight sm:text-4xl">
          Link your Discord.
        </h1>
        <p className="text-sm text-muted-foreground">
          Play the daily map challenge directly in Discord with{' '}
          <span className="font-mono text-foreground">/kzgeo play</span>. Your scores stay tied to
          this account.
        </p>
      </header>

      {linked ? (
        <Card className="border-border/60 bg-[hsl(var(--winner))]/10">
          <CardHeader>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              <CheckCircle2 className="h-3 w-3 text-[hsl(var(--winner))]" />
              Linked
            </div>
            <CardTitle className="font-display text-lg">
              {userProfile?.discordUsername || 'Discord account connected'}
            </CardTitle>
            <CardDescription>
              You're all set. Run <span className="font-mono">/kzgeo play</span> in any server with
              the bot.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <Card className="border-border/60 bg-background/40">
          <CardHeader>
            <CardTitle className="font-display text-lg">Generate a one-time code</CardTitle>
            <CardDescription>
              Run <span className="font-mono">/kzgeo link &lt;code&gt;</span> in Discord within 10
              minutes to bind your account.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {code && !expired ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3 rounded-md border border-heat/40 bg-heat/5 px-4 py-3">
                  <code className="select-all font-mono text-xl font-semibold tracking-[0.2em]">
                    {code}
                  </code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCopy}
                    className="font-mono uppercase tracking-[0.2em]"
                  >
                    <Copy className="mr-1 h-3.5 w-3.5" />
                    Copy
                  </Button>
                </div>
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  <Timer className="h-3 w-3" />
                  Expires in {formatRemaining(remainingSec)}
                </div>
                <p className="text-xs text-muted-foreground">
                  In Discord: <span className="font-mono text-foreground">/kzgeo link {code}</span>
                </p>
              </div>
            ) : (
              <Button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
                className="self-start font-mono uppercase tracking-[0.2em]"
              >
                {generating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {expired ? 'Generate new code' : 'Generate code'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DiscordLink;
